import { atom } from 'jotai';
import { debounce } from 'lodash';

import { log } from '@/lib/utils';
import { logEvent } from '@/pages/api/v1/utils';
import { addGAEvent } from '@/utils/analytics/google/addGAEvent';

import {
  audioElementAtom,
  currentTimeAtom,
  currentTrackAtom,
  durationAtom,
  mutePreferenceAtom,
  queueAtom,
  volumePreferenceAtom,
} from '..';
import {
  getBoundAudioElement,
  getMediaElementSource,
} from '@/features/AudioPlayer/atoms/playback/webAudioGraph';

import type { AudioTrack } from '@/features/AudioPlayer/types';

// Constants for debounce timing and volume calculation
const VOLUME_DEBOUNCE_MS = 500;
const SEEK_DEBOUNCE_MS = 1000;
const VOLUME_PERCENTAGE_MULTIPLIER = 100;

/**
 * TRACKING ATOMS
 * These atoms handle analytics event tracking for the audio player.
 * They require manual triggering (e.g., via the useAudioPlayerTracking hook) to fire logEvent calls.
 */

/**
 * Get the current context (asPath) from the queue for event attribution
 * This ensures events are attributed to the page where the queue was originally loaded
 */
const getCurrentContextAtom = atom((get) => {
  const queue = get(queueAtom);
  return queue.context?.source || '';
});

/**
 * Helper function to get common event metadata from current track
 */
const getTrackMetadata = (track: AudioTrack | null) => {
  if (!track) {
    return {};
  }

  return {
    asset_type: track.type, // 'music' or 'sfx'
    asset_id: track.track_id ?? track.id,
    variant_id: track.id, // Using track ID as variant ID
    // Adaptive-version tracks carry the generated plan's version identifier so
    // player engagement events correlate with a specific version. Omitted for
    // all non-adaptive (catalogue) tracks, leaving their events unchanged.
    ...(track.adaptiveVersion !== undefined && {
      version: track.adaptiveVersion,
    }),
  };
};

/**
 * Play Event Tracking - fires when playback starts
 */
export const playEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const currentTrack = get(currentTrackAtom);
  const context = get(getCurrentContextAtom);

  if (currentTrack && context) {
    log('Tracking play event', {
      trackId: currentTrack.id,
      trackName: currentTrack.name,
      context,
    });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'play',
      asPath: context,
      metadata: {
        ...getTrackMetadata(currentTrack),
        // A play is served by Web Audio only when the current element is wired
        // to a live MediaElementAudioSourceNode. gainNodeAtom is long-lived (it
        // survives resetAudioPlayerAtom and a mid-session flag flip), so it
        // stays non-null after any Web Audio session and would misclassify
        // later html5 plays — the per-element source node is the true signal.
        audio_engine:
          getMediaElementSource() !== null &&
          getBoundAudioElement() === get(audioElementAtom)
            ? 'web_audio'
            : 'html5',
      },
    });
  }
});

/**
 * Volume Change Event Tracking - debounced to avoid spam
 */
const debouncedVolumeEvent = debounce((context: string, volume: number) => {
  // Only track if we have a valid context
  if (context) {
    log('Tracking volume change event', { volume, context });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'click',
      asPath: context,
      metadata: {
        type: 'volume_change',
        value: Math.round(volume * VOLUME_PERCENTAGE_MULTIPLIER), // Convert to percentage and use 'value' field
      },
    });
  }
}, VOLUME_DEBOUNCE_MS); // 500ms debounce

export const volumeEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const volume = get(volumePreferenceAtom);
  const context = get(getCurrentContextAtom);

  debouncedVolumeEvent(context, volume);
});

/**
 * Mute Event Tracking
 */
export const muteEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const isMuted = get(mutePreferenceAtom);
  const context = get(getCurrentContextAtom);

  if (context) {
    log('Tracking mute event', { isMuted, context });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'click',
      asPath: context,
      metadata: {
        type: isMuted ? 'mute' : 'unmute',
      },
    });
  }
});

/**
 * Seek Event Tracking - debounced to avoid spam during scrubbing
 */
const debouncedSeekEvent = debounce(
  (
    context: string,
    currentTime: number,
    duration: number,
    track: AudioTrack
  ) => {
    const percentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    log('Tracking seek event', {
      currentTime,
      duration,
      percentage: Math.round(percentage),
      context,
    });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'click',
      asPath: context,
      metadata: {
        type: 'seek',
        value: Math.round(currentTime), // Use 'value' for seek position in seconds
        text: `${Math.round(percentage)}%`, // Use 'text' for percentage display
        ...getTrackMetadata(track),
      },
    });
  },
  SEEK_DEBOUNCE_MS
); // 1 second debounce for seeking

export const seekEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const currentTime = get(currentTimeAtom);
  const duration = get(durationAtom);
  const currentTrack = get(currentTrackAtom);
  const context = get(getCurrentContextAtom);

  if (currentTrack && context) {
    debouncedSeekEvent(context, currentTime, duration, currentTrack);
  }
});

/**
 * Track Change Event Tracking - fires when a new track is loaded
 */
export const trackChangeEventTrackingAtom = atom<null, [], void>(
  null,
  (get) => {
    const currentTrack = get(currentTrackAtom);
    const context = get(getCurrentContextAtom);

    if (currentTrack && context) {
      log('Tracking track change event', {
        toTrack: currentTrack.id,
        context,
      });

      void logEvent({
        event_type: 'click',
        asPath: context,
        metadata: {
          type: 'track_change',
          ...getTrackMetadata(currentTrack),
        },
      });
    }
  }
);

/**
 * Skip Event Tracking - monitors queue changes to detect skip actions
 */
const previousQueueIndexAtom = atom<number>(-1);

export const skipEventTrackingAtom = atom<null, [], void>(null, (get, set) => {
  const queue = get(queueAtom);
  const previousIndex = get(previousQueueIndexAtom);
  const currentIndex = queue.currentIndex;

  // Only track if there was a meaningful change and we have a previous state
  if (previousIndex >= 0 && previousIndex !== currentIndex) {
    const previousTrackId = queue.trackOrder[previousIndex];
    const previousTrack = previousTrackId
      ? queue.tracks.get(previousTrackId)
      : null;
    const context = get(getCurrentContextAtom);

    if (previousTrack) {
      const skipType =
        currentIndex > previousIndex ? 'skip_next' : 'skip_previous';

      log(`Tracking ${skipType} event`, {
        fromTrack: previousTrack.id,
        fromIndex: previousIndex,
        toIndex: currentIndex,
        context,
      });

      try {
        void logEvent({
          event_type: 'click',
          asPath: context,
          metadata: {
            type: skipType,
            ...getTrackMetadata(previousTrack),
          },
        });
      } catch (error) {
        // Silently handle tracking errors to prevent breaking the app
        log('Skip event tracking failed:', {
          error,
          skipType,
          fromTrack: previousTrack.id,
          fromIndex: previousIndex,
          toIndex: currentIndex,
          context,
        });
      }
    }
  }

  // Update previous index
  set(previousQueueIndexAtom, currentIndex);
});

// Keep the individual atoms for manual triggering if needed
export const skipNextEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const currentTrack = get(currentTrackAtom);
  const context = get(getCurrentContextAtom);

  if (currentTrack) {
    log('Tracking skip next event', {
      trackId: currentTrack.id,
      context,
    });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'click',
      asPath: context,
      metadata: {
        type: 'skip_next',
        ...getTrackMetadata(currentTrack),
      },
    });
  }
});

export const skipPreviousEventTrackingAtom = atom<null, [], void>(
  null,
  (get) => {
    const currentTrack = get(currentTrackAtom);
    const context = get(getCurrentContextAtom);

    if (currentTrack) {
      log('Tracking skip previous event', {
        trackId: currentTrack.id,
        context,
      });

      // GA4 engagement event
      addGAEvent('engagement-musicplayer');

      void logEvent({
        event_type: 'click',
        asPath: context,
        metadata: {
          type: 'skip_previous',
          ...getTrackMetadata(currentTrack),
        },
      });
    }
  }
);

/**
 * Track Completion Event Tracking - fires when track finishes naturally
 */
export const trackCompletionEventTrackingAtom = atom<null, [], void>(
  null,
  (get) => {
    const currentTime = get(currentTimeAtom);
    const duration = get(durationAtom);
    const currentTrack = get(currentTrackAtom);
    const context = get(getCurrentContextAtom);

    if (currentTrack && context) {
      log('Tracking track completion event', {
        trackId: currentTrack.id,
        duration,
        context,
      });

      void logEvent({
        event_type: 'click',
        asPath: context,
        metadata: {
          type: 'track_completed',
          value: Math.round(currentTime), // Use 'value' for completion time
          text: `${Math.round(currentTime)}/${Math.round(duration)}s`, // Use 'text' for time display
          ...getTrackMetadata(currentTrack),
        },
      });
    }
  }
);

/**
 * Pause Event Tracking - fires when playback stops (not at completion)
 */
export const pauseEventTrackingAtom = atom<null, [], void>(null, (get) => {
  const currentTrack = get(currentTrackAtom);
  const currentTime = get(currentTimeAtom);
  const duration = get(durationAtom);
  const context = get(getCurrentContextAtom);

  if (currentTrack && context) {
    log('Tracking pause event', {
      trackId: currentTrack.id,
      pausedAt: currentTime,
      context,
    });

    // GA4 engagement event
    addGAEvent('engagement-musicplayer');

    void logEvent({
      event_type: 'click',
      asPath: context,
      metadata: {
        type: 'pause',
        value: Math.round(currentTime), // Use 'value' for pause position
        text: `${Math.round(currentTime)}/${Math.round(duration)}s`, // Use 'text' for time display
        ...getTrackMetadata(currentTrack),
      },
    });
  }
});
